import { useState } from 'react';
import { useChat } from '../../../../libs/hooks';
import { AlertType } from '../../../../libs/models/AlertType';
import { useAppDispatch, useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { addAlert, setNotifications } from '../../../../redux/features/app/appSlice';
import { ChatState } from '../../../../redux/features/conversations/ChatState';
import { editConversationTitle } from '../../../../redux/features/conversations/conversationsSlice';
import { getErrorDetails } from '../../../utils/TextUtils';
import { NOTIFICATION_TEMPLATES } from '../Notifications/NotificationConstants';

interface IEditChatNameProps {
    name: string;
    chatId: string;
    exitEdits: () => void;
    textButtons?: boolean;
}

export const EditChatName: React.FC<IEditChatNameProps> = ({ name, chatId, exitEdits, textButtons }) => {
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chat = useChat();

    const [title = '', setTitle] = useState<string | undefined>(name);

    /*useEffect(() => {
        if (selectedId !== chatId) exitEdits();
    }, [chatId, exitEdits, selectedId]);*/

    const onSaveTitleChange = async () => {
        if (name !== title) {
            const chatState: ChatState = conversations[selectedId];
            await chat
                .editChat(
                    chatId,
                    title,
                    chatState.memoryBalance,
                    chatState.chatScope,
                    chatState.selectedSites,
                    chatState.hidden,
                    chatState.authoritiveDocuments,
                )
                .then(() => {
                    dispatch(editConversationTitle({ id: chatId, newTitle: title }));
                    dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.EDIT_CHAT }]));
                });
        }
        exitEdits();
    };

    const onClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        setTitle(name);
        exitEdits();
    };

    const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        setTitle(event.target.value);
    };

    const handleSave = (event?) => {
        if (event) {
            // stop the parent element click from being fired if save button is pressed...
            event.stopPropagation();
        }

        onSaveTitleChange().catch((e: any) => {
            const errorMessage = `Unable to retrieve chat to change title. Details: ${getErrorDetails(e)}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
        if (event.key === 'Enter') {
            handleSave();
        }
    };

    return (
        <div
            className={`w-full flex ${textButtons ? 'flex-col' : 'flex-row'} ${textButtons ? 'gap-2' : 'gap-0'}`}
            title={'Edit chat name'}
            aria-label={`Edit chat name for "${name}"`}>
            <input
                title="Chat name"
                type="text"
                value={title}
                onChange={onTitleChange}
                onClick={(e) => e.stopPropagation()}
                id={`input-${chatId}`}
                onKeyDown={handleKeyDown}
                autoFocus
                className="text-sm w-[170px] border border-gray-400 rounded-md p-[2px] bg-white text-black border-b-2 border-purple-600 focus:outline-none text-[14px]"
            />
            {textButtons && (
                <div className="flex self-end gap-2">
                    <button
                        title="Cancel"
                        className="bg-gray-200 text-primary-500 hover:text-primary-700 dark:text-white dark:hover:text-slate-400 ml-1 mb-1"
                        type="button"
                        onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        title="Save"
                        className="bg-primary-200 text-primary-500 hover:text-primary-700 dark:text-white dark:hover:text-slate-400 ml-1 mb-1"
                        type="button"
                        onClick={handleSave}>
                        Save
                    </button>
                </div>
            )}
            {!textButtons && (
                <div className="flex self-end">
                    <button
                        title="Cancel"
                        className="text-primary-500 hover:text-primary-700 dark:text-white dark:hover:text-slate-400 ml-1"
                        type="button"
                        onClick={onClose}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <button
                        title="Save"
                        className="text-primary-500 hover:text-primary-700 dark:text-white dark:hover:text-slate-400"
                        type="button"
                        onClick={handleSave}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
};
